import {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useImperativeHandle,
  forwardRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { HubConnection } from '@microsoft/signalr'
import {
  Box,
  Checkbox,
  Stack,
  Typography,
  CircularProgress,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { queryKeys } from '@/constant/common'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import { useAuthContext } from '@/features/auth/context'
import useTranslation from '@/hooks/useTranslation'
import useIsControlHub from '@/features/config/hooks/controller/useIsControlHub'
import { useUnits } from '@/hooks/useUnits'
import { useSignalR } from '@/signalr/context'
import { ActionSignalR, getGroupName } from '@/types'
import {
  StationUpdateSignalR,
  StationUpdateSignalRItem,
  stationUpdateItemId,
} from '@/types/SignalR/station'
import {
  StationData,
  ChangePositionData,
} from '@/components/google-map/MapPositionStation'
import PinStationOnMap from '@/features/config/components/sidebar/StationsView/PinStationOnMap'
import { useUpdateStation } from '@/features/config/hooks/station'
import DataGrid, { gridRowClasses } from '@/components/grid/DataGrid'
import { commitOpenEditor } from '@/components/grid/commitOpenEditor'
import type { GuidedSaveResult } from '@/features/config/contexts/guided-edit-context'
import type {
  GridApi,
  GridCellCtx,
  GridColumn,
  GridColumnGroup,
  GridColumnState,
  GridSortModelItem,
} from '@/components/grid/types'
import ColumnSettingPopover from '@/components/common/ColumnSettingPopover'
import useSearchStationInfinityList from '../../hooks/station/useSearchStationInfinityList'
import useStationStatusCounts from '../../hooks/station/useStationStatusCounts'
import { useSearchSiteId } from '@/features/admin/hooks/siteInterface'
import locationApi from '@/api/admin/locationApi'
import stationDataConfigApi from '@/api/config/stationDataConfig'
import {
  StationTableViewProps,
  COLUMN_CONFIG,
  COLUMN_GROUPS,
  LOCATION_VIEW,
  COLUMN_STATE_KEY,
} from './column-config'
import { buildColumns, type StationRow } from './column-defs-builder'
import { orderStationRows } from './station-row-order'
import {
  createBulkActionCellRenderer,
  createGPSCoordinatesCellRenderer,
  createUploadPhotoCellRenderer,
} from './cell-renderers'
import { useStationOptions } from './use-station-options'
import toast from 'react-hot-toast'
import { useStationSave, type LateWriteAnswer } from './use-station-save'
import {
  hasCreateHolds,
  subscribeLateCreates,
} from './station-write-registry'
import { isStationComplete } from './station-completeness'
import { useCellValueChanged } from './use-cell-value-changed'
import { useColumnVisibility } from './use-column-visibility'
import { normalizeStationData } from './normalize-station-data'
import useSearchAllDecoders from '@/features/config/hooks/controller/useSearchAllDecoders'
import {
  buildStationDrafts,
  countSavedRowEdits,
  editsMadeAfterWrite,
  hasAutosaveWork,
  pruneSavedDrafts,
  stationDecoderAddresses,
  stationDecoderSignature,
  stationDecodersByAddress,
  stationTypeForDecoder,
} from './station-drafts'
import {
  applyStoredDrafts,
  collectDraftValues,
  loadStationDrafts,
  saveStationDrafts,
} from './station-draft-storage'
import useGuidedAutosave from '@/features/config/hooks/useGuidedAutosave'
import { useSerialisedRunner } from '@/features/config/hooks/useSerialisedSave'
import { isTextContainsModel } from './text-contains-filter'
import { matchesStateBuckets } from './state-bucket-filter'
import { stationStateBucket } from './station-state-buckets'
import {
  buildStationServerFilters,
  buildStationServerSort,
  hasUnmappedServerFilter,
  isClientOnlyFilterActive,
} from './server-filters'
import classes from './styles.module.scss'

const ROW_HEIGHT = 48
const HEADER_HEIGHT = 46
const GROUP_HEADER_HEIGHT = 28
/**
 * Breathing room under the status bar, on top of the bar's own height. 8 left
 * the page 4px scrollable — the grid's own bottom border and the container's
 * padding are not part of the measured bar.
 */
const VIEWPORT_BOUND_GAP = 14

const isNonIrrigation = (row: any) => Number(row?.stationType) === 1

/**
 * How many batches one autosave run may write before handing back to the
 * debounce. 25 rows a batch, so this covers a very large controller in one run
 * while still bounding a server that reports success without writing anything.
 */
const AUTOSAVE_MAX_PASSES = 80

/**
 * How long to wait before trying again after a pass that failed, and how many
 * times. A write can fail for a reason that passes on its own — a timeout, a
 * 500 under load — and the rows behind it should not sit there until the user
 * happens to touch a cell. It can also fail for a reason that never passes, so
 * this gives up after the third attempt and leaves the toolbar to say so.
 */
const AUTOSAVE_RETRY_DELAYS_MS = [5_000, 15_000, 30_000]

/** How many recent writes the rows-per-second estimate is read off. */
const SAVE_RATE_WINDOW = 20

// SignalR StationUpdate sends the 0-based StationState (station id + status);
// map it to the list's state string (matches the BE GetEnumDescription used by
// the list and getById). Note: the list stores "Started" (not "Active").
const STATION_STATUS_NAME: Record<number, string> = {
  0: 'Inactive',
  1: 'Idling',
  2: 'Wait Schedule',
  3: 'Wait Start',
  4: 'Started',
  5: 'Wait Stop',
  6: 'Soaking',
  7: 'Error',
  8: 'Rain Soaking',
}
const ERROR_STATUS = 7

/**
 * The column the list is sorted by until the user says otherwise. Named once
 * because the incomplete-rows lift has to recognise it (SCRUM-1010 QC).
 */
const DEFAULT_SORT_FIELD = 'description'

export interface StationTableViewRef {
  handleSave: () => Promise<void>
  /**
   * SCRUM-1032 QC round 3, item 7 — write what is ready, say nothing.
   *
   * What the guided setup calls on the way out of the step. `handleSave`
   * refuses the whole batch and names the missing fields when any row is
   * incomplete, which on this list is the normal state of affairs — a thousand
   * rows nobody fills in one sitting. Leaving the step is not the moment to be
   * told that, so this writes the rows that are ready and leaves the rest
   * staged, exactly as the pause autosave does.
   */
  /** Answers `{ failed, total }` — write failures only; see runAutosave. */
  flushReadyRows: () => Promise<GuidedSaveResult>
  handleOpenColumnVisibility: (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => void
  handleBasicListView: () => void
  isBasicListActive: boolean
  applyPreset: (fields: string[]) => void
  applyColumns: (columns: GridColumnState[]) => void
  removeRows: (ids: string[]) => void
}

const StationTableView = forwardRef<StationTableViewRef, StationTableViewProps>(
  (
    {
      onViewItem,
      onEditItem,
      onDeleteItem,
      bulkEditMode = false,
      onToggleBulkEdit,
      keyword,
      onSelectedRowsChanged,
      stations: externalStations,
      interfaceId: propInterfaceId,
      draftFromDecoders = false,
      onProgressChange,
      onIncompleteFilterChange,
    },
    ref,
  ) => {
    const { interfaceId: authInterfaceId } = useAuthContext()
    // Prefer an explicitly passed interface (guided setup, SCRUM-1032) over the
    // globally-selected one; normalize to number | null so downstream call sites
    // keep their exact types. Falls back to AuthContext for the normal config screen.
    const interfaceId =
      propInterfaceId != null ? Number(propInterfaceId) : authInterfaceId
    const { t } = useTranslation()
    const { getHubConnect, joinGroup, leaveGroup } = useSignalR()
    const isDark = useSelector(selectTheme) === mode_type.dark
    // Only a control hub has more than one controller to name in the picker.
    const { data: isControlHub } = useIsControlHub(Number(interfaceId))
    const queryClient = useQueryClient()

    // useUnits hands back fresh closures every render, so they are pinned to
    // the measurement system itself — otherwise every column would rebuild on
    // each render.
    const {
      isMetric,
      convertFromMetricV2,
      convertToMetricV2,
      getImperialUnitLabel,
    } = useUnits()
    const units = useMemo(
      () => ({ convertFromMetricV2, convertToMetricV2, getImperialUnitLabel }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [isMetric],
    )

    // External-data mode: the caller (Map View) supplies the exact rows to show
    // (already filtered to the visible-on-map stations). Skip the internal
    // paginated fetch + SignalR; the caller keeps the list fresh.
    const externalMode = externalStations !== undefined

    const {
      areaOptionsData,
      subAreaOptionsData,
      setSubAreaOptionsData,
      plantOptionsData,
      soilOptionsData,
      emitterOptionsData,
      pipeOptionsData,
      etZoneOptionsData,
      controllerOptionsData,
      subAreaCacheRef,
      optionsSettled,
      controllersLoaded,
    } = useStationOptions(interfaceId)

    const [filterModel, setFilterModel] = useState<Record<string, unknown>>({})

    // Column filters the endpoint can apply itself (SCRUM-1071), mapped from
    // the labels the grid filters on to the ids the endpoint takes.
    //
    // Never in the wizard: its rows are drafted from the decoders that have no
    // station yet, and "has no station" is decided from this very list. A
    // narrowed list would look like every filtered-out station is unstationed
    // and draft a duplicate row for each one. The wizard filters client-side
    // over the whole list it already holds.
    const serverFilters = useMemo(
      () =>
        draftFromDecoders
          ? {}
          : buildStationServerFilters(filterModel, {
              areaOptionsData,
              subAreaOptionsData,
              plantOptionsData,
              soilOptionsData,
              emitterOptionsData,
              pipeOptionsData,
            }),
      [
        draftFromDecoders,
        filterModel,
        areaOptionsData,
        subAreaOptionsData,
        plantOptionsData,
        soilOptionsData,
        emitterOptionsData,
        pipeOptionsData,
      ],
    )

    // What the client still has to do itself, and therefore needs every row for.
    //
    // Sorting is client-side in the grid, so a sort over a page reorders that
    // page and nothing else. State, ET Zone and the two "contains" columns have
    // no usable endpoint filter (see server-filters.ts), so they narrow only
    // what has been scrolled past. Either one on its own is what "sort and
    // filter don't work" meant, and either one is enough to need the list whole
    // — including alongside a server filter, since the client pass would
    // otherwise run over one page of the server's result.
    const [sortModel, setSortModel] = useState<GridSortModelItem[]>([])
    const handleSortChanged = useCallback((model: GridSortModelItem[]) => {
      // The grid reports a new array each render; only a real change should
      // re-key the query and refetch.
      setSortModel((previous) =>
        JSON.stringify(previous) === JSON.stringify(model) ? previous : model,
      )
    }, [])

    /**
     * Rows still missing something the save needs — the ones the user has left
     * to do. Counted with the staged edits laid over the rows, so it falls as
     * they are filled in rather than once a save has caught up.
     */
    const [incompleteOnly, setIncompleteOnly] = useState(false)

    // The wizard sorts client-side over the whole list it already holds.
    const serverSort = useMemo(
      () => (draftFromDecoders ? null : buildStationServerSort(sortModel)),
      [draftFromDecoders, sortModel],
    )

    // The grid opens sorted by station name, so a sort is always active. Left
    // to the client that would mean always fetching the whole list — which is
    // the thing this is here to stop — so a sort the endpoint knows is sent to
    // it, and the grid is told not to reorder the result.
    const needsWholeList =
      !draftFromDecoders &&
      (incompleteOnly ||
        (sortModel.length > 0 && !serverSort) ||
        isClientOnlyFilterActive(filterModel) ||
        hasUnmappedServerFilter(filterModel, serverFilters))
    const {
      data: listStationData,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      isFetching: isFetchingStations,
      isLoading: isLoadingStations,
    } = useSearchStationInfinityList(
      interfaceId,
      {
        enabled: !externalMode,
        refetchInterval: false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
      },
      keyword,
      // Page size. The wizard always takes the list whole.
      //
      // The wizard has to: its rows are drafted from the decoders that have no
      // station yet, so a page at a time would draft a duplicate for every
      // station below the fold — and the list only stopped looking complete
      // once there were enough saved stations to page.
      //
      // /config has to for a different reason. This grid has no
      // `serverDatasource`, so DataGrid runs it in client mode and sorts and
      // filters whatever is currently in `rows`. Paged at 20, sorting a column
      // reordered 20 rows out of hundreds and a filter matched nothing that had
      // not been scrolled past — which reads, correctly, as sort and filter
      // being broken. The same reason the status pills undercounted.
      //
      // /config pages instead — a controller with a thousand stations should
      // not fetch all of them to show the first screen — and takes the whole
      // list only while a search, sort or filter is active, because those have
      // to see every row to be right.
      draftFromDecoders || keyword || needsWholeList ? 0 : undefined,
      serverFilters,
      serverSort,
    )

    const gridApiRef = useRef<GridApi<StationRow> | null>(null)
    const submitValidatedRef = useRef(false)
    const bulkEditModeRef = useRef(bulkEditMode)
    bulkEditModeRef.current = bulkEditMode

    // Base rows. Unsaved edits are NOT held here — they live in the grid's
    // pending-edit buffer, and the grid renders base ⊕ pending. That is what
    // lets a live update, a refetch or another scrolled-in page replace this
    // list without touching a single unsaved change.
    const [rows, setRows] = useState<StationRow[]>([])
    const rowsRef = useRef(rows)
    rowsRef.current = rows

    /**
     * When the station list on screen was requested — the earliest page, since
     * a station created mid-read may be missing from it. A create with no known
     * outcome is decided only by a read that started after it (SCRUM-1032
     * review, see `station-write-registry.ts`).
     */
    const listReadStartedAt = useMemo(() => {
      const stamps = (listStationData?.pages ?? [])
        .map((page: any) => Number(page?.requestedAt))
        .filter((stamp: number) => Number.isFinite(stamp))
      return stamps.length ? Math.min(...stamps) : 0
    }, [listStationData])
    const listReadStartedAtRef = useRef(listReadStartedAt)
    listReadStartedAtRef.current = listReadStartedAt

    /**
     * The list has been read since this grid mounted. The cached list a
     * remount starts from can predate a create sent just before Back, and a
     * draft built against it looks unstationed.
     */
    const [stationsReadOnEntry, setStationsReadOnEntry] = useState(false)
    useEffect(() => {
      if (!isFetchingStations && !isLoadingStations && listStationData)
        setStationsReadOnEntry(true)
    }, [isFetchingStations, isLoadingStations, listStationData])

    const [cellHighlights, setCellHighlights] = useState<
      Record<string, Record<string, string>>
    >({})
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
    const [isSaving, setIsSaving] = useState(false)
    const [openGPSModal, setOpenGPSModal] = useState(false)
    const [currentGPSStation, setCurrentGPSStation] = useState<
      StationData | undefined
    >(undefined)
    const [dataLocation, setDataLocation] = useState<LOCATION_VIEW[]>()

    const { data: dataSite } = useSearchSiteId(interfaceId + '', {
      PageSize: 0,
    })
    // The grid writes row by row and refreshes once at the end; see quietCache.
    const { mutateAsync: editStationAsync } = useUpdateStation({
      showNoti: false,
      quietCache: true,
      showError: false,
    })

    const {
      columnVisibilityAnchor,
      visibleColumns,
      isBasicListActive,
      handleOpenColumnVisibility,
      handleCloseColumnVisibility,
      handleToggleColumn,
      handleShowAllColumns,
      handleHideAllColumns,
      handleBasicListView,
      handleApplyPreset,
      applySavedView,
    } = useColumnVisibility(gridApiRef)

    // --- Rows ----------------------------------------------------------------

    const normalizedRowData = useMemo(() => {
      const source = externalMode
        ? (externalStations ?? [])
        : (listStationData?.pages?.flatMap((page: any) => page.list || []) ??
          [])
      if (source.length === 0) return []

      // One station, one row. Pages can overlap — a create landing between two
      // page requests shifts every station after it — and a repeated station
      // is not a harmless double: two rows on one decoder is exactly what the
      // conflict check flags, so it reported a list-wide problem that did not
      // exist.
      const seen = new Set<string>()
      const unique = source.filter((station: any) => {
        const id = String(station?.id ?? '')
        if (!id) return true
        if (seen.has(id)) return false
        seen.add(id)
        return true
      })

      return normalizeStationData(unique, t)
    }, [externalMode, externalStations, listStationData, t])

    // --- Drafts from the decoders (SCRUM-1032 / SCRUM-1040) -------------------

    // Every decoder marked Irrigation or Non-Irrigation is a station waiting to
    // be named, and after the guided reset there is nothing else to build the
    // list from. See `station-drafts.ts`.
    const { data: allDecoders, refetch: refetchDecoders } =
      useSearchAllDecoders(Number(interfaceId), undefined, {
        enabled: draftFromDecoders && Number.isFinite(Number(interfaceId)),
      })

    // The decoder list is held for the sitting (a thousand rows, `staleTime:
    // Infinity`), so returning to this step after editing a decoder would
    // otherwise show the list as it was when the wizard opened. Re-read on
    // entry; the rebuild below only acts if the purposes actually moved.
    //
    // Nothing is written until that read is back: drafts built from the cached
    // decoders could carry a purpose or a decoder that has since changed
    // (SCRUM-1032 review). A failed read is tried again on the retry delays.
    const [decodersFresh, setDecodersFresh] = useState(false)
    useEffect(() => {
      if (!draftFromDecoders) return
      let cancelled = false
      let timer: ReturnType<typeof setTimeout> | undefined
      const read = (attempt: number) => {
        refetchDecoders()
          .then((result) => {
            if (cancelled) return
            if (!result.isError) {
              setDecodersFresh(true)
              return
            }
            const delay = AUTOSAVE_RETRY_DELAYS_MS[attempt]
            if (delay != null) timer = setTimeout(() => read(attempt + 1), delay)
          })
          .catch(() => undefined)
      }
      read(0)
      return () => {
        cancelled = true
        if (timer) clearTimeout(timer)
      }
      // Mount only: a refetch per render would be a thousand rows a keystroke.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftFromDecoders])

    const [decoderDrafts, setDecoderDrafts] = useState<StationRow[]>([])
    const decoderDraftsRef = useRef(decoderDrafts)
    decoderDraftsRef.current = decoderDrafts
    /**
     * What each written draft sent, by decoder address. The draft stays on
     * screen and editable until the list re-reads; whatever differs from this
     * when it is replaced was typed after the write, and follows the row to its
     * new station instead of vanishing with the draft (round 7, item 2).
     */
    const writtenDraftEditsRef = useRef<Map<string, Record<string, unknown>>>(
      new Map(),
    )
    // A pass can outlive the debounce; without this a second timer could start
    // creating the same rows again before the first finished.
    const isAutosavingRef = useRef(false)
    // The drafts as the decoders produced them, before anything was restored
    // or typed — the yardstick for "did the user change this?".
    const draftBaselineRef = useRef<Record<string, any>>({})
    /** `interfaceId` plus the decoders' purposes — the inputs to the drafts. */
    const draftedForRef = useRef<string | null>(null)

    useEffect(() => {
      if (!draftFromDecoders || !allDecoders) return
      // Only once the whole station list is in hand: the list pages, and
      // drafting against page one alone would offer a second row for every
      // station still below the fold.
      if (isLoadingStations || hasNextPage) return

      // Rebuilt when the decoders change, not once per controller: the user can
      // go back a step, mark a decoder non-irrigation, and return, and the rows
      // have to follow. Identical data leaves this untouched.
      const signature = `${interfaceId}:${stationDecoderSignature(allDecoders)}`
      if (draftedForRef.current === signature) return
      const isRebuild = draftedForRef.current !== null
      draftedForRef.current = signature

      // A rebuild throws away the draft rows and their temp ids, so anything
      // typed this sitting is written down first and restored below with
      // everything from previous ones. Without this, going back one step to fix
      // a purpose would cost the user every row they had filled in since.
      if (isRebuild) {
        const pendingEdits = gridApiRef.current?.getPendingEdits() ?? new Map()
        const staged = decoderDraftsRef.current.map((row) => ({
          ...row,
          ...(pendingEdits.get(String(row.id)) ?? {}),
        }))
        saveStationDrafts(
          Number(interfaceId),
          collectDraftValues(staged, draftBaselineRef.current),
        )
        // The temp ids are about to be handed to different decoders, so the
        // edits filed under them cannot be allowed to follow.
        staged.forEach((row) =>
          gridApiRef.current?.removePendingEdit(String(row.id)),
        )
      }

      // What the user typed is laid back over the freshly built drafts: the
      // decoder half comes from the decoders every time, so a decoder renamed
      // or repurposed since arrives current.
      const fresh = buildStationDrafts({
        decoders: allDecoders,
        stations: normalizedRowData,
      })
      // Kept as the baseline: what is stored later is only what differs from
      // this, so an untouched row costs nothing and a renamed decoder wins.
      draftBaselineRef.current = Object.fromEntries(
        fresh.map((draft) => [draft.decoderAddress, draft]),
      )
      setDecoderDrafts(
        applyStoredDrafts(
          fresh,
          loadStationDrafts(Number(interfaceId)),
        ) as StationRow[],
      )
    }, [
      draftFromDecoders,
      allDecoders,
      isLoadingStations,
      hasNextPage,
      interfaceId,
      normalizedRowData,
    ])

    useEffect(() => {
      if (decoderDrafts.length === 0) {
        setRows(normalizedRowData)
        return
      }
      // A draft whose station now exists is dropped rather than left standing
      // beside it. Only then: a row written a second ago but not yet in the
      // list stays on screen, with its values and its saved tint, instead of
      // blinking out and returning when the refetch lands.
      const staged = pruneSavedDrafts(decoderDrafts, normalizedRowData)
      if (staged.length !== decoderDrafts.length) {
        const kept = new Set(staged.map((draft) => String(draft.id)))
        const api = gridApiRef.current
        let carried = false
        decoderDrafts
          .filter((draft) => !kept.has(String(draft.id)))
          .forEach((draft) => {
            const draftId = String(draft.id)
            const address = String(draft.decoderAddress ?? '')
            const after = editsMadeAfterWrite(
              api?.getPendingEdits().get(draftId) as
                | Record<string, unknown>
                | undefined,
              writtenDraftEditsRef.current.get(address),
            )
            const station = normalizedRowData.find(
              (row) => String(row?.decoderAddress ?? '') === address,
            )
            if (station && Object.keys(after).length > 0) {
              api?.setPendingEdit(String(station.id), after as Partial<StationRow>)
              carried = true
            }
            // The temp id is handed to another decoder on the next rebuild, so
            // nothing may stay filed under it.
            api?.removePendingEdit(draftId)
            writtenDraftEditsRef.current.delete(address)
          })
        // Arms the autosave for the carried values, as an edit would.
        if (carried) setEditVersion((version) => version + 1)
        setDecoderDrafts(staged)
      }
      setRows([...normalizedRowData, ...staged])
    }, [normalizedRowData, decoderDrafts])

    /** The rows as the grid draws them, for checks that must see unsaved picks. */
    const getMergedRows = useCallback((): StationRow[] => {
      const pendingEdits = gridApiRef.current?.getPendingEdits()
      if (!pendingEdits || pendingEdits.size === 0) return rowsRef.current
      return rowsRef.current.map((row) => {
        const edit = pendingEdits.get(String(row.id))
        return edit ? { ...row, ...edit } : row
      })
    }, [])

    // Filters the grid carries but does not apply — a value set it can match
    // itself, a "contains" search or a status group it cannot.
    const displayedRows = useMemo(() => {
      const textFilters = Object.entries(filterModel).filter(([, model]) =>
        isTextContainsModel(model),
      ) as [string, { contains: string }][]
      const stateFilter = filterModel.state

      if (textFilters.length === 0 && !stateFilter) return rows

      return rows.filter(
        (row) =>
          matchesStateBuckets(row?.state, stateFilter) &&
          textFilters.every(([field, model]) =>
            String(row?.[field] ?? '')
              .toLowerCase()
              .includes(model.contains.toLowerCase()),
          ),
      )
    }, [rows, filterModel])

    const getRowId = useCallback((row: StationRow) => String(row.id), [])

    /**
     * SCRUM-1010 QC, 21 Sep — the rows that still need something come first,
     * and rows still being created stay at the bottom whatever the sort.
     *
     * The lift is off while the user has a sort of their own: the default
     * order is Station Name ascending, and anything else is a column they
     * clicked, which this must not quietly reorder around. `incompleteKeysRef`
     * rather than the value, so the callback stays stable while the set moves
     * with every staged edit.
     *
     * And off until the whole list is in hand. This screen pages at 20, and
     * lifting within the rows that happen to have loaded would move a row to
     * "the top" that is only the top of page three — the list would reshuffle
     * under the user as they scrolled, which reads as a broken sort rather
     * than as help. On a controller with hundreds of stations the reliable way
     * to reach them is "Check for missing data", which narrows the list to
     * exactly those rows.
     *
     * What is lifted is a **snapshot**, not the live set. Reading it live moved
     * a row to the top the instant an edit made it incomplete — while the user
     * was typing in it (Wei Woon, 5 Oct: "Editing station row, the editted cell
     * will went to the top row"). The snapshot is retaken when the list is
     * re-read, which is the same rule the "still to fill in" filter follows.
     */
    const liftIncomplete =
      !hasNextPage &&
      (sortModel.length === 0 ||
        (sortModel.length === 1 &&
          sortModel[0]?.field === DEFAULT_SORT_FIELD &&
          sortModel[0]?.sort === 'asc'))
    const liftIncompleteRef = useRef(liftIncomplete)
    liftIncompleteRef.current = liftIncomplete

    /**
     * The rows the lift holds at the top, frozen until the list is re-read.
     * Filled by the effect below, which runs after `incompleteKeys` has been
     * recomputed for the same render.
     */
    const liftedKeysRef = useRef<Set<string>>(new Set())

    const postSort = useCallback(
      (list: StationRow[]) =>
        orderStationRows(list, {
          isNew: (row) => !!row?.__isNew || Number(row?.id) < 0,
          needsAction: liftIncompleteRef.current
            ? (row) => liftedKeysRef.current.has(String(row?.id))
            : null,
        }),
      [],
    )

    // --- Bulk edit -----------------------------------------------------------

    // Leaving bulk edit discards every staged change; entering it starts from a
    // clean buffer. Save empties the buffer itself once the server has the data.
    useEffect(() => {
      if (bulkEditMode) return
      gridApiRef.current?.clearPendingEdits()
      setCellHighlights({})
      setSelectedIds(new Set())
      submitValidatedRef.current = false
    }, [bulkEditMode])

    const highlightCell = useCallback(
      (rowId: string, field: string, color = '#ffebee') => {
        setCellHighlights((previous) => ({
          ...previous,
          [rowId]: { ...(previous[rowId] ?? {}), [field]: color },
        }))
      },
      [],
    )

    const clearCellHighlight = useCallback((rowId: string, field: string) => {
      setCellHighlights((previous) => {
        if (!previous[rowId]?.[field]) return previous
        const forRow = { ...previous[rowId] }
        delete forRow[field]
        const next = { ...previous }
        if (Object.keys(forRow).length === 0) delete next[rowId]
        else next[rowId] = forRow
        return next
      })
    }, [])

    // --- Live status updates -------------------------------------------------

    const applyStationUpdates = useCallback(
      (items: StationUpdateSignalRItem[]) => {
        if (bulkEditModeRef.current || items.length === 0) return

        const statusById = new Map(
          items.map((item) => [stationUpdateItemId(item), Number(item.status)]),
        )

        setRows((previous) =>
          previous.map((row) => {
            const status = statusById.get(Number(row.id))
            if (status === undefined) return row

            const isError = status === ERROR_STATUS
            // The list row's `state` is the status STRING.
            const nextState = STATION_STATUS_NAME[status] ?? row.state
            // Keep the existing error code/name while in error; clear otherwise.
            const nextErrorCode = isError ? row.errorCode : 0
            const nextErrorName = isError ? row.errorName : null

            if (
              row.state === nextState &&
              row.errorCode === nextErrorCode &&
              row.errorName === nextErrorName
            ) {
              return row
            }

            return {
              ...row,
              state: nextState,
              errorCode: nextErrorCode,
              errorName: nextErrorName,
            }
          }),
        )
      },
      [],
    )

    /**
     * Fills in what a StationUpdate leaves out.
     *
     * The broadcast carries a station id and a status and nothing else, which
     * covers the pill but not the two things around it:
     *
     * - A station moving INTO Error arrives with no code or description, so the
     *   row would read a bare "Error" where a server-loaded row reads
     *   "Code: 3000 - Line unit failure detected". One request for this
     *   interface's error rows fills every one of them in, however many changed.
     * - The status summary is counted by the endpoint, not from `rows`, so it
     *   does not move when a row does. Invalidating its queries re-reads it.
     *
     * Both are skipped in bulk edit, for the same reason `applyStationUpdates`
     * is: the grid holds unsaved edits and must not be written from under them.
     */
    const refreshLiveStatusDetails = useCallback(
      async (items: StationUpdateSignalRItem[]) => {
        if (bulkEditModeRef.current || !interfaceId) return

        queryClient.invalidateQueries([queryKeys.stations, 'status-count'])

        if (!items.some((item) => Number(item.status) === ERROR_STATUS)) return

        try {
          const { data } = await stationDataConfigApi.getAll({
            interfaceId: Number(interfaceId),
            PageIndex: 1,
            PageSize: 0,
            StateIds: [ERROR_STATUS],
          })
          const list = Array.isArray(data) ? data : data?.data
          if (!Array.isArray(list)) return

          const errorById = new Map<number, any>(
            list.map((station: any) => [Number(station.id), station]),
          )
          setRows((previous) =>
            previous.map((row) => {
              const fresh = errorById.get(Number(row.id))
              if (!fresh) return row
              const nextErrorCode = fresh.errorCode ?? 0
              const nextErrorName = fresh.errorName ?? null
              if (
                row.errorCode === nextErrorCode &&
                row.errorName === nextErrorName
              ) {
                return row
              }
              return { ...row, errorCode: nextErrorCode, errorName: nextErrorName }
            }),
          )
        } catch {
          // Leave the pill as it is — it already says Error, only the code is
          // missing, and the next list refetch will bring it.
        }
      },
      [interfaceId, queryClient],
    )

    // StationUpdate broadcasts are GROUP-targeted, so the interface group must
    // be joined before any events arrive. Bursts are coalesced into one update.
    useEffect(() => {
      // In external-data mode the caller (Map View) owns the data + realtime.
      if (!interfaceId || externalMode) return

      const groupName = getGroupName(Number(interfaceId))
      let connection: HubConnection | undefined
      let debounceTimer: ReturnType<typeof setTimeout> | undefined
      let isMounted = true
      const pendingItems = new Map<number, number>()

      const handleStationUpdate = (stationUpdate: StationUpdateSignalR) => {
        if (Number(stationUpdate?.interfaceId) !== Number(interfaceId)) return
        if (!stationUpdate?.items?.length) return
        stationUpdate.items.forEach((item) =>
          pendingItems.set(stationUpdateItemId(item), Number(item.status)),
        )
        if (debounceTimer) clearTimeout(debounceTimer)
        debounceTimer = setTimeout(() => {
          const items = [...pendingItems.entries()].map(
            ([stationId, status]) => ({
              id: stationId,
              status,
            }),
          )
          pendingItems.clear()
          applyStationUpdates(items)
          void refreshLiveStatusDetails(items)
        }, 300)
      }

      const startSignalR = async () => {
        try {
          connection = await getHubConnect()
          if (!isMounted) return
          // Ref-counted join so the sidebar popup leaving its group doesn't
          // silence the grid (both share one connection).
          await joinGroup(groupName)
          connection.on(ActionSignalR.STATION_UPDATE, handleStationUpdate)
        } catch (error) {
          console.error('Station status SignalR subscription failed:', error)
        }
      }

      startSignalR()

      return () => {
        isMounted = false
        if (debounceTimer) clearTimeout(debounceTimer)
        // Remove only THIS handler — the hub connection is shared.
        connection?.off(ActionSignalR.STATION_UPDATE, handleStationUpdate)
        leaveGroup(groupName)
      }
    }, [
      interfaceId,
      externalMode,
      getHubConnect,
      joinGroup,
      leaveGroup,
      applyStationUpdates,
      refreshLiveStatusDetails,
    ])

    // --- Save ----------------------------------------------------------------

    const handleNewStationsCreated = useCallback(() => {
      queryClient.invalidateQueries([queryKeys.stations])
    }, [queryClient])

    /**
     * The bulk save's own progress, for the toolbar readout on /config
     * (SCRUM-1010 QC item 2). The guided autosave reports through
     * `onProgressChange` below; this is the same readout for the other screen.
     */
    const [bulkSaveProgress, setBulkSaveProgress] = useState<{
      done: number
      total: number
    } | null>(null)
    const bulkSaveStartedRef = useRef(0)
    const handleBulkSaveProgress = useCallback(
      (progress: { done: number; total: number } | null) => {
        if (progress && progress.done === 0) bulkSaveStartedRef.current = Date.now()
        setBulkSaveProgress(progress)
      },
      [],
    )

    /**
     * One refresh at the end of a bulk save, in place of the per-row
     * invalidation `useUpdateStation` used to do (SCRUM-1010 QC item 1).
     */
    const handleSaveSettled = useCallback(() => {
      queryClient.invalidateQueries([queryKeys.stations])
    }, [queryClient])

    const handleSaved = useCallback((freshRows: StationRow[]) => {
      if (freshRows.length === 0) return
      const freshById = new Map(freshRows.map((row) => [Number(row.id), row]))
      setRows((previous) =>
        previous.map((row) => {
          const fresh = freshById.get(Number(row.id))
          return fresh ? { ...row, ...fresh } : row
        }),
      )
    }, [])

    // After a per-row create the API returns only a success flag (not the new
    // id), so drop the temp row and pull the freshly-created station from the
    // server (correct id + normalized decoder/name).
    const handleRowCreated = useCallback(
      async (tempRow: StationRow) => {
        setRows((previous) => previous.filter((row) => row.id !== tempRow.id))

        try {
          const { data: freshData } = await stationDataConfigApi.getAll({
            interfaceId: Number(interfaceId),
            PageSize: 0,
            PageIndex: 1,
          })
          const list = Array.isArray(freshData) ? freshData : freshData?.data
          if (!Array.isArray(list)) return
          const known = new Set(rowsRef.current.map((row) => Number(row.id)))
          const fresh = normalizeStationData(list, t).filter(
            (station: any) => !known.has(Number(station.id)),
          )
          if (fresh.length === 0) return
          setRows((previous) => [...previous, ...fresh])
        } catch {
          queryClient.invalidateQueries([queryKeys.stations])
        }
      },
      [interfaceId, queryClient, t],
    )

    /**
     * Decoders whose station has been written this sitting.
     *
     * The station list pages at 20, so a station created a second ago is
     * usually not in the rows the grid has loaded — pruning the drafts against
     * those rows alone would leave the written ones on screen. This is the
     * other half of that answer, and it does not depend on paging.
     */
    const writtenAddressesRef = useRef<Set<string>>(new Set())

    /**
     * Stations written this sitting, by decoder address — the tint that says a
     * row has landed, and the record that stops it being written twice.
     *
     * Keyed by address rather than by row id, and never cleared. A draft's id
     * is a temp counter that a rebuild hands to a different decoder, and the
     * rows come and go as the list refetches — a mark filed under one of those
     * ids came unstuck as soon as anything moved, which is what turned a saved
     * row amber again and offered it back to the next pass. The address is the
     * one thing about the row that does not change.
     */
    const [savedAddresses, setSavedAddresses] = useState<Set<string>>(new Set())
    /**
     * When the last few rows landed, for the rate the ETA is read off.
     *
     * Every station is written through to the SRC controller, one at a time
     * and with no bulk endpoint behind it, so a long list is a long wait
     * whatever this page does. What it can do is say how long — measured from
     * the rows actually going through, since the per-row time varies by
     * controller and by how busy it is.
     */
    const saveTimestampsRef = useRef<number[]>([])
    const [saveRate, setSaveRate] = useState(0)

    const markRowSaved = useCallback(
      (
        rowId: string,
        decoderAddress: string,
        written?: Record<string, unknown>,
      ) => {
        const address = String(decoderAddress ?? '')
        if (!address || address === '-') return
        if (Number(rowId) < 0 && written) {
          writtenDraftEditsRef.current.set(address, written)
        }

        // The last twenty, so the estimate follows a controller that speeds up
        // or slows down instead of averaging the whole run flat.
        const stamps = saveTimestampsRef.current
        stamps.push(Date.now())
        if (stamps.length > SAVE_RATE_WINDOW) stamps.shift()
        if (stamps.length > 1) {
          const elapsed = stamps[stamps.length - 1] - stamps[0]
          setSaveRate(elapsed > 0 ? ((stamps.length - 1) * 1000) / elapsed : 0)
        }
        // Written to the ref in the same breath, not at the end of the pass: a
        // draft that is rebuilt or re-read mid-batch must already look written,
        // or it is sent a second time.
        writtenAddressesRef.current.add(address)
        setSavedAddresses((previous) => {
          if (previous.has(address)) return previous
          const next = new Set(previous)
          next.add(address)
          return next
        })
      },
      [],
    )

    /**
     * SCRUM-1032 QC item 15b — the two write paths queue behind one another.
     *
     * Stations are written both by the pause autosave and by the bulk save the
     * wizard calls when the user presses Next. `isAutosavingRef` below only
     * stops the autosave overlapping *itself*; the two paths could still run at
     * once, and both then read the same staged drafts and create them twice —
     * which is the "72 sharing a decoder" in the QC report.
     */
    const { run: runSerialised, isRunning: isSavingStations } =
      useSerialisedRunner()

    // Bumped by every edit, so the debounce restarts on a burst of typing and
    // the effects below can see changes held in the grid's own buffer.
    const [editVersion, setEditVersion] = useState(0)
    /** For edits staged outside the grid's editors (GPS picker, photo). */
    const bumpEditVersion = useCallback(
      () => setEditVersion((version) => version + 1),
      [],
    )
    /** Rows the server refused, and what it said. Cleared as each one lands. */
    const [rowErrors, setRowErrors] = useState<Map<string, string>>(new Map())

    /**
     * A write that outlived its pass has answered. A success is looked at
     * again (an update re-reads its buffer, a create re-reads the list); a
     * refusal is filed against the row with what the server said, and is not
     * re-armed from here — see `onLateWriteSettled`.
     */
    const handleLateWrite = useCallback(
      ({ kind, rowId, ok, message }: LateWriteAnswer) => {
        setRowErrors((previous) => {
          if (ok ? !previous.has(rowId) : previous.get(rowId) === message)
            return previous
          const next = new Map(previous)
          if (ok) next.delete(rowId)
          else next.set(rowId, message ?? '')
          return next
        })
        if (!ok) return
        // A create's list re-read comes from `subscribeLateCreates` below,
        // which also covers an answer that arrives after this grid is gone.
        if (kind === 'update') bumpEditVersion()
      },
      [bumpEditVersion],
    )

    // A create that outlived its pass answered — perhaps sent by an earlier
    // mount of this grid, before Back. Re-read the list so the draft is
    // retired (or, if it was refused, written) without waiting for an edit.
    useEffect(
      () =>
        subscribeLateCreates(interfaceId, () => {
          handleNewStationsCreated()
          bumpEditVersion()
        }),
      [interfaceId, handleNewStationsCreated, bumpEditVersion],
    )

    /**
     * Whether a station payload can be built from what is loaded.
     *
     * The option lists check plant, soil, emitter, ET zone and controller ids;
     * before they are in, the ids went up blank (SCRUM-1032 review). In the
     * wizard the drafts also need the entry read of the decoders and of the
     * station list, or a stale cache is written as fact.
     */
    const writeReady =
      optionsSettled &&
      controllersLoaded &&
      (!draftFromDecoders || (decodersFresh && stationsReadOnEntry))
    const writeReadyRef = useRef(writeReady)
    writeReadyRef.current = writeReady
    const isWriteReady = useCallback(() => writeReadyRef.current, [])
    /** Still on its way, as opposed to never coming (a list failed to load). */
    const writeReadyPendingRef = useRef(false)
    writeReadyPendingRef.current =
      !optionsSettled ||
      (draftFromDecoders && (!decodersFresh || !stationsReadOnEntry))

    const { handleSave, handleSaveRow, saveCompleteRows } = useStationSave({
      apiRef: gridApiRef,
      rowsRef,
      submitValidatedRef,
      setIsSaving,
      interfaceId,
      editStationAsync,
      plantOptionsData,
      soilOptionsData,
      emitterOptionsData,
      etZoneOptionsData,
      controllerOptionsData,
      onToggleBulkEdit,
      highlightCell,
      onSaved: handleSaved,
      onNewStationsCreated: handleNewStationsCreated,
      onRowCreated: handleRowCreated,
      onRowSaved: markRowSaved,
      onLateWriteSettled: handleLateWrite,
      onSaveSettled: handleSaveSettled,
      onSaveProgress: handleBulkSaveProgress,
      writtenAddressesRef,
      listReadStartedAtRef,
      writeReady: isWriteReady,
    })

    // --- Autosave (SCRUM-1032) ------------------------------------------------

    const [isAutosaving, setIsAutosaving] = useState(false)
    /**
     * A pass is sending rows. Narrower than `isAutosaving`: a pass that finds
     * nothing ready never sets it, so it does not flash the grid read-only
     * under someone typing (round 7, item 2).
     */
    const [isAutosaveWriting, setIsAutosaveWriting] = useState(false)
    /**
     * SCRUM-1032 QC round 7 follow-up — the lock above says why it is there.
     *
     * The grid goes read-only while a pass writes, and a click or a key in it
     * then did nothing at all: the user could not tell a pause from a broken
     * cell. Said once per attempt, under one toast id so a burst of keys is
     * one notice.
     */
    const sayLockedWhileWriting = useCallback(
      (event: ReactKeyboardEvent | ReactMouseEvent) => {
        if (!isAutosaveWriting || !bulkEditMode) return
        const target = event.target as HTMLElement | null
        const inCell = Boolean(target?.closest?.('[role="gridcell"]'))
        // Selecting a row is not an edit, and still works while locked.
        if (target?.closest?.('input[type="checkbox"], [role="checkbox"]')) return
        if ('key' in event) {
          // The filter boxes in the header stay usable; only edits are held.
          if (!inCell && target?.closest?.('input, textarea')) return
          const modified = event.ctrlKey || event.metaKey || event.altKey
          const editKey =
            !modified &&
            (event.key.length === 1 ||
              ['Enter', 'F2', 'Backspace', 'Delete'].includes(event.key))
          if (!editKey) return
        } else if (!inCell) {
          return
        }
        toast(t('stationsEditPausedWhileSaving'), {
          id: 'stations-edit-paused',
        })
      },
      [isAutosaveWriting, bulkEditMode, t],
    )
    /**
     * Staged edits on saved stations. Read in an effect, after the grid has
     * rendered: during this component's render the grid's buffer can still
     * be the one from before the edit that caused the render.
     */
    const [savedRowEditCount, setSavedRowEditCount] = useState(0)
    /**
     * Moves once the grid has committed an edit. Anything computed from the
     * grid's buffer during render (the incomplete count) can read the buffer
     * from before the edit; this re-runs it against the committed one. An edit
     * that restores the saved value is dropped by the grid after the page has
     * already rendered, and without this the row stayed "still to fill in".
     */
    const [bufferTick, setBufferTick] = useState(0)
    useEffect(() => {
      setSavedRowEditCount(
        countSavedRowEdits(gridApiRef.current?.getPendingEdits()),
      )
      setBufferTick((tick) => tick + 1)
    }, [editVersion, isAutosaving])
    /**
     * SCRUM-1032 QC round 7 — saved stations whose edit emptied a required
     * field, with what to say about each. Replaced whole on every pass (each
     * pass sees every edited saved row), and emptied once no saved-row edit is
     * left — undoing the edit leaves nothing to write, so no pass would say so.
     * Kept apart from `rowErrors`, which only a successful write clears.
     */
    const [refusedRows, setRefusedRows] = useState<Map<string, string>>(
      () => new Map(),
    )
    useEffect(() => {
      if (savedRowEditCount === 0)
        setRefusedRows((previous) => (previous.size ? new Map() : previous))
    }, [savedRowEditCount])
    const refusedNote = refusedRows.values().next().value ?? null


    // An error belongs to a row on screen. Drafts are rebuilt from the decoders
    // whenever the purposes move — with fresh temp ids — so an error held
    // against an id that no longer exists counts towards "N to fix" while the
    // filter that narrows to those rows finds nothing to show.
    useEffect(() => {
      setRowErrors((previous) => {
        if (previous.size === 0) return previous
        const alive = new Set(rows.map((row) => String(row.id)))
        const next = new Map(previous)
        previous.forEach((_message, rowId) => {
          if (!alive.has(rowId)) next.delete(rowId)
        })
        return next.size === previous.size ? previous : next
      })
    }, [rows])
    /**
     * SCRUM-1032 QC round 7 follow-up — a saved station with nothing left to
     * write has nothing left to fix.
     *
     * A write error is cleared by that row's next successful pass. Two ways
     * out need no pass: the edit is undone (typed back to the saved value),
     * or an update that outlived its pass answers 200 late and retires the
     * edit itself. Both left "1 could not be saved" and one more "to fix" on
     * the toolbar until some other save ran. Drafts keep theirs: their values
     * are the row, not an edit to it.
     */
    useEffect(() => {
      // No grid, no buffer to read: an absent buffer is not an empty one.
      const pending = gridApiRef.current?.getPendingEdits()
      if (!pending) return
      setRowErrors((previous) => {
        if (previous.size === 0) return previous
        const next = new Map(previous)
        previous.forEach((_message, rowId) => {
          if (Number(rowId) > 0 && !pending.has(rowId)) next.delete(rowId)
        })
        return next.size === previous.size ? previous : next
      })
    }, [bufferTick])
    // The toolbar's "N could not be saved: …" is read off the rows still in
    // error, so the count and the message always describe the same rows.
    const failedRowCount = rowErrors.size
    const failedRowMessage = rowErrors.values().next().value ?? null
    /** Narrows the list to the rows that need attention. */
    const [issuesOnly, setIssuesOnly] = useState(false)
    /**
     * Hides the stations already written, so what is left on screen is what is
     * left to do. On a list of a thousand the saved rows are the ones the user
     * has finished with, and scrolling past them to find where they got to is
     * most of the work of coming back to this step.
     */
    const [hideSaved, setHideSaved] = useState(false)
    const incompleteKeys = useMemo(() => {
      const keys = new Set<string>()
      getMergedRows().forEach((row) => {
        if (!isStationComplete(row)) keys.add(String(row?.id))
      })
      return keys
      // `editVersion` is what moves when the grid's own edit buffer is written.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, editVersion, bufferTick, getMergedRows])
    /**
     * Fixed when the filter is switched on, like the hide-saved one: a row
     * completed while it is showing must not vanish from under the user.
     */
    const [incompleteShown, setIncompleteShown] = useState<Set<string> | null>(null)
    const incompleteKeysRef = useRef(incompleteKeys)
    incompleteKeysRef.current = incompleteKeys

    /**
     * The lift's snapshot, retaken when the list itself is re-read and when a
     * change of sort switches the lift on. Deliberately **not** keyed on
     * `editVersion`: an edit must not reorder the row being edited.
     *
     * Computed during render rather than in an effect, because the grid sorts
     * in the same pass — an effect would leave the first sort after every read
     * working from the previous snapshot.
     */
    const liftedKeys = useMemo(
      () =>
        liftIncompleteRef.current
          ? new Set(incompleteKeysRef.current)
          : new Set<string>(),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [rows, liftIncomplete],
    )
    liftedKeysRef.current = liftedKeys

    /**
     * SCRUM-1157 — saved stations whose Type has been set to follow their
     * decoder, by row id, with the type they were given.
     *
     * A station whose decoder changed between Irrigation and Non-Irrigation
     * used to sit red until the user corrected its Type by hand, and nothing
     * stopped the setup finishing with it wrong. The decoder's purpose is the
     * source of truth, so the Type now follows it: staged as an edit, which the
     * autosave writes like any other. Held here because the grid's edit buffer
     * is not readable until it has rendered — without it the row reads as
     * mismatched again for a render and is staged a second time.
     */
    const [autoTyped, setAutoTyped] = useState<Map<string, string>>(new Map())

    /**
     * Saved stations whose decoder is no longer an irrigation or non-irrigation
     * decoder — repurposed to a master valve or a pump after the station was
     * made. The station still exists and still holds that decoder, so the
     * decoder cannot be given to a valve either: it is a conflict only the user
     * can settle, and the only useful thing to do is show it.
     */
    const stationConflicts = useMemo(() => {
      const repurposed = new Set<string>()
      const typeMismatch = new Set<string>()
      /**
       * Two stations on one decoder. The API takes the address as the thing a
       * station occupies, so once a pair exists it refuses to update either —
       * both rows are frozen until one is deleted, and nothing else in the app
       * will say so. Flagged like the others, which is also what gives the row
       * back its DELETE.
       */
      const duplicated = new Set<string>()

      const seenAddress = new Map<string, string[]>()
      normalizedRowData.forEach((row: any) => {
        const address = String(row?.decoderAddress ?? '')
        if (!address || address === '-') return
        seenAddress.set(address, [
          ...(seenAddress.get(address) ?? []),
          String(row.id),
        ])
      })
      seenAddress.forEach((ids) => {
        if (ids.length > 1) ids.forEach((id) => duplicated.add(id))
      })

      /** Mismatched stations still to be retyped, with the type to give them. */
      const retype = new Map<string, string>()

      if (!draftFromDecoders || !allDecoders)
        return { repurposed, typeMismatch, duplicated, retype }

      const stationAddresses = stationDecoderAddresses(allDecoders)
      const byAddress = stationDecodersByAddress(allDecoders)

      normalizedRowData.forEach((row: any) => {
        const address = String(row?.decoderAddress ?? '')
        if (!address || address === '-') return

        // The decoder is no longer a station at all.
        if (!stationAddresses.has(address)) {
          repurposed.add(String(row.id))
          return
        }

        // Still a station, but the other kind. The decoder's purpose is the
        // source of truth — the station type was derived from it — so a
        // station left on the old one is stale, not a deliberate override.
        const expected = stationTypeForDecoder(byAddress.get(address))
        if (expected && String(row?.stationType ?? '') !== expected) {
          // Already retyped and waiting on the autosave: not a conflict.
          if (autoTyped.get(String(row.id)) === expected) return
          typeMismatch.add(String(row.id))
          retype.set(String(row.id), expected)
        }
      })

      return { repurposed, typeMismatch, duplicated, retype }
    }, [draftFromDecoders, allDecoders, normalizedRowData, autoTyped])

    // Stage the decoder's type on every station left on the other one. See
    // `autoTyped` above.
    useEffect(() => {
      const api = gridApiRef.current
      if (!api || stationConflicts.retype.size === 0) return
      stationConflicts.retype.forEach((stationType, rowId) => {
        api.setPendingEdit(rowId, { stationType })
      })
      setAutoTyped((previous) => {
        const next = new Map(previous)
        stationConflicts.retype.forEach((stationType, rowId) =>
          next.set(rowId, stationType),
        )
        return next
      })
    }, [stationConflicts])

    // Re-armed once the grid has taken the staged types, so the autosave and
    // the incomplete count read the buffer with them in it.
    useEffect(() => {
      if (autoTyped.size > 0) setEditVersion((version) => version + 1)
    }, [autoTyped])

    // Rows that keep their DELETE in the wizard: the only way out of either
    // conflict is to remove the station.
    const conflictingStationIds = useMemo(
      () =>
        new Set([
          ...stationConflicts.repurposed,
          ...stationConflicts.duplicated,
        ]),
      [stationConflicts],
    )

    /** A row worth looking at: flagged by the decoders, or refused by the API. */
    const hasIssue = useCallback(
      (row: StationRow) => {
        const id = String(row?.id)
        return (
          stationConflicts.repurposed.has(id) ||
          stationConflicts.typeMismatch.has(id) ||
          stationConflicts.duplicated.has(id) ||
          rowErrors.has(id) ||
          refusedRows.has(id)
        )
      },
      [stationConflicts, rowErrors, refusedRows],
    )

    /**
     * What state a row is in, said with the whole row rather than with one
     * amber cell.
     *
     * On a list of a thousand, "which of these have I done?" is the question
     * being asked every few seconds, and the per-cell tint answered a much
     * narrower one — which *value* was edited. Four states, most urgent first:
     *
     * - red: something is wrong with the row and the user has to settle it.
     * - green: written to the server this sitting.
     * - amber: a draft — a decoder with no station yet, still to be filled in.
     * - plain: a station that was already there when the step opened.
     */
    /**
     * SCRUM-1032 QC item 15a — what is actually wrong with a flagged row.
     *
     * The four things that flag a row are not cell problems: the row shares a
     * decoder with another, its decoder is no longer a station, its type does
     * not match, or the server refused it. Wei Woon read the red as "fix these
     * cells" and found every cell filled, because they were. The row now says
     * which of the four it is, and the server's own message where there is one.
     */
    const issueReason = useCallback(
      (row: StationRow): string | null => {
        const id = String(row?.id)
        // The server's own words first: they are the most specific thing
        // available, and the only one the user could not have worked out.
        const serverError = rowErrors.get(id)
        if (serverError) return serverError
        const refusedReason = refusedRows.get(id)
        if (refusedReason) return refusedReason
        if (stationConflicts.duplicated.has(id))
          return t('stationsIssueDuplicated')
        if (stationConflicts.repurposed.has(id))
          return t('stationsIssueRepurposed')
        if (stationConflicts.typeMismatch.has(id))
          return t('stationsIssueTypeMismatch')
        return null
      },
      [rowErrors, refusedRows, stationConflicts, t],
    )

    const rowClass = useCallback(
      ({ row }: { row: StationRow }) => {
        if (hasIssue(row)) return gridRowClasses.conflict
        // A written row drops the amber for the saved tint the moment its own
        // request lands, rather than at the end of the batch it was in.
        if (savedAddresses.has(String(row?.decoderAddress ?? ''))) {
          return gridRowClasses.saved
        }
        // Never written: the row exists in this tab and nowhere else.
        if (Number(row?.id) < 0) return gridRowClasses.new
        return undefined
      },
      [hasIssue, savedAddresses],
    )

    // Narrowing to the problems is the only way to find them in a list of a
    // thousand: an error toast names a count, not a row.
    const rowFilter = useCallback(
      (row: StationRow) => {
        if (issuesOnly && !hasIssue(row)) return false
        // Until the set is fixed (the list is still arriving) every row stays:
        // hiding them all while the filter warms up reads as "no results".
        if (incompleteOnly && incompleteShown && !incompleteShown.has(String(row?.id))) {
          return false
        }
        // A saved row with a problem stays: hiding the finished ones must not
        // hide the ones that need fixing.
        if (hideSaved && Number(row?.id) > 0 && !hasIssue(row)) return false
        return true
      },
      [issuesOnly, hideSaved, hasIssue, incompleteOnly, incompleteShown],
    )

    const issueCount =
      stationConflicts.repurposed.size +
      stationConflicts.typeMismatch.size +
      stationConflicts.duplicated.size +
      rowErrors.size +
      [...refusedRows.keys()].filter((id) => !rowErrors.has(id)).length

    // Nothing left to look at: drop back to the whole list rather than leaving
    // the user staring at an empty grid wondering where their rows went.
    useEffect(() => {
      if (issuesOnly && issueCount === 0) setIssuesOnly(false)
    }, [issuesOnly, issueCount])

    /**
     * On /config the list pages at twenty, so the count is only true once the
     * whole list is in hand — which switching the filter on arranges. Until
     * then the page shows the control without a number.
     *
     * Measured against the server's own total rather than the query's paging
     * flags: right after the filter is switched on those still describe the
     * paged query it replaced, and reading them said "all here" over twenty
     * rows — which switched the filter straight back off again.
     */
    const serverStationTotal = Number(
      (listStationData?.pages?.[0] as any)?.totalCount ?? 0,
    )
    const incompleteCountKnown =
      draftFromDecoders ||
      (!isLoadingStations &&
        !hasNextPage &&
        !isFetchingNextPage &&
        rows.length >= serverStationTotal)
    useEffect(() => {
      if (!incompleteOnly) {
        setIncompleteShown(null)
        return
      }
      // Not before the whole list is in hand: taken at the click, the set held
      // whatever the first page happened to carry, and filtering to it emptied
      // the grid.
      if (!incompleteCountKnown) return
      setIncompleteShown((previous) => previous ?? new Set(incompleteKeysRef.current))
    }, [incompleteOnly, incompleteCountKnown])

    useEffect(() => {
      onIncompleteFilterChange?.({
        count: incompleteKeys.size,
        known: incompleteCountKnown,
        only: incompleteOnly,
        onToggle: () => setIncompleteOnly((only) => !only),
      })
    }, [
      onIncompleteFilterChange,
      incompleteKeys,
      incompleteCountKnown,
      incompleteOnly,
    ])

    useEffect(() => {
      if (!incompleteOnly || !incompleteCountKnown) return
      if (incompleteKeys.size > 0) return
      // Nothing to show: say so rather than leaving an empty grid behind.
      setIncompleteOnly(false)
      if (!draftFromDecoders) toast.success(t('stationsNoMissingData'))
    }, [incompleteOnly, incompleteCountKnown, incompleteKeys, draftFromDecoders, t])


    // Stations on the controller, against the rows still waiting to become one.
    //
    // The server's own total, not the rows in hand: this list pages at 20, so
    // counting what is loaded reported "20 of 20 saved" on a controller with
    // four hundred stations written. The rows retired this sitting are the
    // floor, for the moment between a create landing and the count catching up.
    const serverStationCount = Number(
      (listStationData?.pages?.[0] as any)?.totalCount ?? 0,
    )
    const savedCount = Math.max(
      serverStationCount,
      normalizedRowData.length,
      writtenAddressesRef.current.size,
    )

    /**
     * The whole job on this controller: one station per station-purpose
     * decoder. A fixed number for the sitting, which is what a denominator has
     * to be.
     *
     * "x of y" used to be saved + still-to-write, and those two do not move in
     * step: writing twenty rows took twenty off the second while the first sat
     * on a station count the server had not caught up with yet, so the total
     * fell by twenty. A total that goes down as work is done is worse than no
     * total at all.
     */
    const stationDecoderTotal = useMemo(
      () => stationDecoderAddresses(allDecoders).size,
      [allDecoders],
    )

    // Reported to the page, which shows it beside Expand. The readout belongs
    // on the toolbar row rather than on a line of its own above the grid —
    // that line was height the list wanted.
    // The drafts still to write. A row written this pass is retired from
    // `decoderDrafts` only when the pass ends, so counting the list itself made
    // the total climb by one for every row saved — and a percentage against a
    // moving total is a percentage that barely moves.
    const pendingCount = useMemo(
      () =>
        decoderDrafts.filter(
          (draft) => !savedAddresses.has(String(draft.decoderAddress ?? '')),
        ).length,
      [decoderDrafts, savedAddresses],
    )

    /** Rows staged and not yet written — what a running pass is working on. */
    const stagedChangeCount = useMemo(
      () => gridApiRef.current?.getPendingEdits().size ?? 0,
      // The buffer is mutated through the grid api, so there is nothing to
      // depend on but the counter every edit bumps, plus the start of a pass.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [editVersion, isAutosaving],
    )

    // /config: the bulk save's progress, while one is running.
    useEffect(() => {
      if (draftFromDecoders) return
      if (!bulkSaveProgress) {
        onProgressChange?.(null)
        return
      }
      const { done, total } = bulkSaveProgress
      const elapsedSeconds = (Date.now() - bulkSaveStartedRef.current) / 1000
      const rate = done > 0 && elapsedSeconds > 0 ? done / elapsedSeconds : 0
      const left = Math.max(0, total - done)
      onProgressChange?.({
        saved: done,
        pending: left,
        total,
        inFlight: left,
        etaSeconds: rate > 0 && left > 0 ? Math.round(left / rate) : null,
        issues: 0,
        issuesOnly: false,
        onToggleIssuesOnly: () => undefined,
        incomplete: 0,
        incompleteOnly: false,
        onToggleIncompleteOnly: () => undefined,
        hideSaved: false,
        onToggleHideSaved: () => undefined,
        saving: true,
        failed: 0,
        error: null,
        repurposed: 0,
        typeMismatch: 0,
        duplicated: 0,
      })
    }, [draftFromDecoders, bulkSaveProgress, onProgressChange])

    useEffect(() => {
      if (!draftFromDecoders) return
      onProgressChange?.({
        // Capped at the total: a controller carrying stations for decoders
        // that are no longer stations would otherwise read "990 of 979".
        saved: Math.min(savedCount, stationDecoderTotal || savedCount),
        pending: pendingCount,
        total: stationDecoderTotal || savedCount + pendingCount,
        // Only while a run is going: a rate measured minutes ago describes
        // nothing, and a countdown that is not counting is worse than none.
        etaSeconds:
          isAutosaving && saveRate > 0 && pendingCount > 0
            ? Math.round(pendingCount / saveRate)
            : null,
        issues: issueCount,
        issuesOnly,
        onToggleIssuesOnly: () => setIssuesOnly((only) => !only),
        incomplete: incompleteKeys.size,
        incompleteOnly,
        onToggleIncompleteOnly: () => setIncompleteOnly((only) => !only),
        hideSaved,
        onToggleHideSaved: () => setHideSaved((hidden) => !hidden),
        saving: isAutosaving,
        /**
         * SCRUM-1032 QC round 6, item 3 — the rows this pass is writing.
         *
         * `saved`/`total` above are the whole list, which is what the step is
         * judged on. While a pass runs they answer the wrong question: after
         * filling and saving 479 stations, editing two of them again read as
         * "Saving… 479 of 479" — accurate about the list, silent about the two,
         * and impossible to tell from a save that has hung.
         *
         * Read from the grid's staged rows rather than counted inside the save
         * loop, so it is right from the first frame of the pass instead of
         * after the first batch comes back.
         */
        inFlight: stagedChangeCount,
        failed: failedRowCount,
        error: failedRowMessage,
        refused: refusedNote,
        repurposed: stationConflicts.repurposed.size,
        typeMismatch: stationConflicts.typeMismatch.size,
        duplicated: stationConflicts.duplicated.size,
      })
    }, [
      draftFromDecoders,
      onProgressChange,
      savedCount,
      pendingCount,
      stationDecoderTotal,
      saveRate,
      issueCount,
      issuesOnly,
      incompleteKeys,
      incompleteOnly,
      hideSaved,
      isAutosaving,
      stagedChangeCount,
      failedRowCount,
      failedRowMessage,
      refusedNote,
      stationConflicts,
    ])

    /**
     * Writes the ready rows, batch after batch, until there are none left.
     *
     * One pass writes 25. Ending there and waiting for the autosave debounce to
     * come back cost 2.5 seconds of nothing for every 25 rows — on a
     * five-hundred-station controller, a minute and a half of deliberate
     * idling on top of the writes themselves. A batch that fills up is a queue
     * that has more in it, so the next one starts straight away.
     *
     * The loop stops on the first pass that writes nothing (nothing ready, or
     * everything left is failing), on any failure — which needs the list
     * re-read before anything else is tried — and at a hard cap, so a server
     * answering "success" without doing anything cannot spin here forever.
     */
    // A run that stops on a failure has to come back on its own: the autosave
    // is armed by a change — a new edit, a new row — and a failure is neither,
    // so without this the batch simply stopped and waited for the user to type
    // something. Counted, so a row the server will never accept is retried
    // three times rather than forever.
    const retryCountRef = useRef(0)
    const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    /** Re-reads the list once a held create's settle window has passed. */
    const holdReadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(
      () => () => {
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current)
        if (holdReadTimerRef.current) clearTimeout(holdReadTimerRef.current)
      },
      [],
    )

    // A new read of the list may decide a held create: ask for a pass.
    useEffect(() => {
      if (!draftFromDecoders || !listReadStartedAt) return
      if (hasCreateHolds(interfaceId)) setEditVersion((version) => version + 1)
    }, [draftFromDecoders, listReadStartedAt, interfaceId])

    const runAutosave = useCallback(async (): Promise<GuidedSaveResult> => {
      if (isAutosavingRef.current) return { failed: 0, total: 0 }
      isAutosavingRef.current = true
      setIsAutosaving(true)
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
      let wroteAnything = false
      let stoppedOnFailure = false
      /**
       * SCRUM-1032 QC round 6, item 4 — what this flush answers the wizard with.
       *
       * **Write failures only.** This path is the quiet flush: it writes the
       * rows that are ready and deliberately leaves the incomplete ones staged
       * (see `flushReadyRows` and SetUpGuided round 3, item 7). Counting those
       * as failures would refuse every forward move off a step that still has
       * a row to fill in — on a 479-station list that is a trap, and it is the
       * opposite of why the quiet flush exists.
       */
      let failedWrites = 0
      /** Writes still out after the last pass; see `stillOut`. */
      let writesStillOut = 0
      let attemptedWrites = 0
      /**
       * SCRUM-1032 QC round 7 — saved stations whose edit emptied a required
       * field. Not write failures (nothing was sent, and retrying cannot help),
       * but they are counted in the answer: unlike an unfinished draft, this is
       * an existing station the user has broken, and moving on would leave the
       * edit unsaved with no word — the same rule the Hydraulic step follows.
       * Kept per row across passes so a row is counted once.
       */
      const refusedThisRun = new Set<string>()
      try {
        for (let pass = 0; pass < AUTOSAVE_MAX_PASSES; pass++) {
          const {
            created,
            failed,
            updated,
            createdAddresses,
            createdRowIds,
            updatedIds,
            failures,
            refused,
            stillOut,
            held,
            heldUntil,
          } = await saveCompleteRows(undefined, () => {
            // An editor still open here means the timer started this pass:
            // the Next/rail flush closes it before it gets this far. The user
            // is mid-value, so it is neither closed nor locked out — locking
            // would throw the value away, and closing it committed half a
            // name (SCRUM-1032 round 7 review). Whatever they commit lands in
            // the buffer after this pass read it, and only the fields this
            // pass sent are dropped afterwards, so it is kept for the next.
            //
            // Decided here rather than by removing the autosave's editor-hold
            // cap: that cap is what stops an idle open editor from holding
            // every guided grid's save for ever.
            if (gridApiRef.current?.isEditingCell()) return
            setIsAutosaveWriting(true)
          })

          // Kept per row so the list can show which ones are stuck, and the
          // filter has something to narrow to. A row that succeeds this pass
          // drops off it — the line here used to be a no-op over the addresses,
          // so a row that failed once and then landed kept its error for the
          // rest of the sitting. That is what left the toolbar counting rows to
          // fix that the filter could not find: the row was gone, the error was
          // not.
          setRowErrors((previous) => {
            const next = new Map(previous)
            createdRowIds.forEach((rowId) => next.delete(rowId))
            updatedIds.forEach((rowId) => next.delete(rowId))
            failures.forEach(({ rowId, message }) => next.set(rowId, message))
            return next
          })
          refused.forEach(({ rowId }) => refusedThisRun.add(rowId))
          // A held create is a write whose outcome is still unknown: it counts
          // against Next exactly as one still out does.
          writesStillOut = stillOut + held
          if (heldUntil !== null) {
            // Only a list read that starts after the hold can decide it, and
            // nothing else would ask for one.
            if (holdReadTimerRef.current) clearTimeout(holdReadTimerRef.current)
            holdReadTimerRef.current = setTimeout(
              () => {
                holdReadTimerRef.current = null
                handleNewStationsCreated()
              },
              Math.max(0, heldUntil - Date.now()) + 1_000,
            )
          }
          // Every pass reports the whole current set, so it replaces the last.
          setRefusedRows(
            new Map(
              refused.map(({ rowId, fields }) => [
                rowId,
                t('stationsIssueIncomplete', { fields: fields.join(', ') }),
              ]),
            ),
          )

          // A written row keeps its place, its values and the saved tint until
          // the list comes back carrying the real station. Retiring it the
          // moment its request landed is what emptied the list as it saved —
          // eight hundred rows disappearing one by one and returning at the
          // end. The address is recorded so nothing writes it twice; the rows
          // effect drops the draft when its station actually arrives.
          createdAddresses.forEach((address) =>
            writtenAddressesRef.current.add(address),
          )

          if (created > 0 || updated > 0) wroteAnything = true
          failedWrites += failed
          attemptedWrites += created + updated + failed

          // Nothing moved, or something is stuck: either way the next attempt
          // belongs to the debounce, not to this loop. A failure in particular
          // has to wait for the list to be re-read before the row is tried again.
          if (failed > 0) {
            stoppedOnFailure = true
            break
          }
          if (created + updated === 0) break
        }
      } finally {
        isAutosavingRef.current = false
        setIsAutosaving(false)
        setIsAutosaveWriting(false)
        // One read of the list at the end, in place of one per batch: the rows
        // written in between are already retired by address. Skipped entirely
        // when nothing was written, so a debounce that finds nothing ready
        // costs no request at all.
        if (wroteAnything) handleNewStationsCreated()

        // Progress means the situation changed, so the next failure starts its
        // count again rather than inheriting one from ten minutes ago.
        if (wroteAnything) retryCountRef.current = 0

        if (stoppedOnFailure) {
          const delay = AUTOSAVE_RETRY_DELAYS_MS[retryCountRef.current]
          if (delay != null) {
            retryCountRef.current += 1
            // Bumping the version is how the autosave is asked for another go:
            // it re-arms the debounce exactly as an edit would.
            retryTimerRef.current = setTimeout(() => {
              retryTimerRef.current = null
              setEditVersion((version) => version + 1)
            }, delay)
          }
        }
      }

      return {
        // A write still out counts against the step until it answers:
        // walking on past it would lose the edit unseen if it were refused.
        failed: failedWrites + refusedThisRun.size,
        total: attemptedWrites + refusedThisRun.size + writesStillOut,
        stillOut: writesStillOut,
      }
    }, [saveCompleteRows, handleNewStationsCreated, t])

    useGuidedAutosave({
      // Not until the station list is actually in hand. A draft is only a
      // draft because no station exists for its decoder, and that is decided
      // against the list — so autosaving before it arrives writes rows for
      // decoders that already have one. The server answers those with
      // "Emitter does not exist", which is no help at all, and the duplicates
      // it does accept have to be deleted by hand afterwards.
      //
      // Nor until the payload's inputs are fresh (`writeReady`): the option
      // lists, and the entry reads of the decoders and the stations.
      enabled:
        draftFromDecoders &&
        bulkEditMode &&
        !isLoadingStations &&
        !hasNextPage &&
        draftedForRef.current !== null &&
        writeReady,
      // Drafts to write, or edits to stations that already exist — with no
      // drafts left, the second used to arm nothing (round 7, item 5).
      hasPendingChanges: hasAutosaveWork(decoderDrafts.length, savedRowEditCount),
      // Drafts are kept in localStorage and rebuilt on return, so only edits
      // to saved stations are lost by leaving the page. Counting the drafts
      // raised "Leave site?" on every visit with one unfinished row.
      losesOnLeave: savedRowEditCount > 0,
      version: editVersion,
      save: () => runSerialised(runAutosave),
      // An open editor is the user mid-value; the save would close it.
      isEditing: () => gridApiRef.current?.isEditingCell() ?? false,
      // This list reports its own progress, with a row count and a Stop.
      reportsOwnState: true,
      isBusy: isSavingStations,
    })

    // The rows that are not writable yet exist nowhere but this tab, so they
    // are kept locally until they are complete enough to be sent. Written on
    // the same beat as the autosave rather than per keystroke.
    useEffect(() => {
      if (!draftFromDecoders) return
      // Not before the drafts have been built. On mount there are none yet, and
      // writing that emptiness would clear the very entry the build is about to
      // read — the restore then found nothing, every time.
      if (draftedForRef.current === null) return
      const pendingEdits = gridApiRef.current?.getPendingEdits() ?? new Map()
      const staged = decoderDrafts.map((row) => ({
        ...row,
        ...(pendingEdits.get(String(row.id)) ?? {}),
      }))
      saveStationDrafts(
        Number(interfaceId),
        collectDraftValues(staged, draftBaselineRef.current),
      )
    }, [draftFromDecoders, decoderDrafts, editVersion, interfaceId])

    const { onCellValueChanged: onCellValueChangedBase } = useCellValueChanged({
      getMergedRows,
      areaOptionsData,
      interfaceId,
      setSubAreaOptionsData,
      subAreaCacheRef,
      isNonIrrigation,
      clearCellHighlight,
    })

    // Every accepted edit restarts the autosave debounce. The edits themselves
    // live in the grid's buffer, which this component cannot watch directly.
    const onCellValueChanged = useCallback(
      (event: Parameters<typeof onCellValueChangedBase>[0]): void | false => {
        const result = onCellValueChangedBase(event)
        if (result === false) return false
        // A Type set by hand is checked against the decoder afresh.
        if (event.field === 'stationType') {
          setAutoTyped((previous) => {
            if (!previous.has(event.rowId)) return previous
            const next = new Map(previous)
            next.delete(event.rowId)
            return next
          })
        }
        // Moves the incomplete count below, wherever the grid is used.
        setEditVersion((version) => version + 1)
        if (!draftFromDecoders) return
        // The user changing something is a new situation: a row that failed
        // three times gets its attempts back, since this edit may be the fix.
        retryCountRef.current = 0
      },
      [onCellValueChangedBase, draftFromDecoders],
    )

    // --- GPS picker ----------------------------------------------------------

    useEffect(() => {
      ;(async () => {
        if (dataSite && dataSite.list.length > 0) {
          const res = await locationApi.getBySiteId(dataSite.list[0].id, {
            PageSize: 0,
          })
          if (res) setDataLocation(res.data.data)
        }
      })()
    }, [dataSite])

    const handleOpenGPSPicker = useCallback(
      (station: StationRow) => {
        setCurrentGPSStation({
          id: station.id,
          siteId: Number(dataSite?.list[0]?.id),
          siteName: dataSite?.list[0]?.name || null,
          locationId: (dataLocation && dataLocation[0]?.id) || null,
          locationName: (dataLocation && dataLocation[0]?.name) || null,
          floorId: station.floorId || null,
          grsCoordinatedLatitude: station.grsCoordinatedLatitude || null,
          grsCoordinatedLongitude: station.grsCoordinatedLongitude || null,
          areaId: station.subArea?.parentArea?.id || null,
          areaName: station.subArea?.parentArea?.name || null,
          subAreaId: station.subAreaId || null,
          subAreaName:
            typeof station.subArea === 'object'
              ? station.subArea?.name
              : station.subArea || null,
        })
        setOpenGPSModal(true)
      },
      [dataLocation, dataSite],
    )

    const handleCloseGPSPicker = useCallback(() => {
      setOpenGPSModal(false)
      setCurrentGPSStation(undefined)
    }, [])

    const handleChangePosition = useCallback(
      (positionData: ChangePositionData) => {
        if (currentGPSStation) {
          // Staged like any other edit, so it is saved with them.
          gridApiRef.current?.setPendingEdit(String(currentGPSStation.id), {
            grsCoordinatedLatitude: positionData.grsCoordinatedLatitude,
            grsCoordinatedLongitude: positionData.grsCoordinatedLongitude,
            floorId: positionData.floorId,
            siteId: positionData.siteId,
            siteName: positionData.siteName,
            locationId: positionData.locationId,
            locationName: positionData.locationName,
          })
          // The picker stages outside the grid's editors, so nothing else
          // tells the autosave there is something to write.
          bumpEditVersion()
        }
        handleCloseGPSPicker()
      },
      [currentGPSStation, handleCloseGPSPicker, bumpEditVersion],
    )

    const handleDiscardRow = useCallback((id: string) => {
      gridApiRef.current?.removePendingEdit(id)
      setRows((previous) => previous.filter((row) => String(row.id) !== id))
    }, [])

    // --- Selection -----------------------------------------------------------

    const toggleRowSelected = useCallback(
      (rowId: string, selected: boolean) => {
        setSelectedIds((previous) => {
          const next = new Set(previous)
          if (selected) next.add(rowId)
          else next.delete(rowId)
          return next
        })
      },
      [],
    )

    const selectableIds = useMemo(
      () =>
        displayedRows
          .filter((row) => Number(row.id) > 0)
          .map((row) => String(row.id)),
      [displayedRows],
    )

    const toggleAllSelected = useCallback(
      (selected: boolean) => {
        setSelectedIds(selected ? new Set(selectableIds) : new Set())
      },
      [selectableIds],
    )

    useEffect(() => {
      onSelectedRowsChanged?.([...selectedIds])
    }, [selectedIds, onSelectedRowsChanged])

    const SelectHeaderCheckbox = useCallback(() => {
      const selected = selectableIds.filter((id) => selectedIds.has(id)).length
      return (
        <Box className={classes.checkboxCell} data-no-cell-edit>
          <Checkbox
            size="small"
            checked={
              selectableIds.length > 0 && selected === selectableIds.length
            }
            indeterminate={selected > 0 && selected < selectableIds.length}
            onChange={(event) => toggleAllSelected(event.target.checked)}
          />
        </Box>
      )
    }, [selectableIds, selectedIds, toggleAllSelected])

    const SelectCellCheckbox = useCallback(
      ({ row, rowId }: GridCellCtx<StationRow>) => {
        if (Number(row?.id) < 0) return null
        return (
          <Box className={classes.checkboxCell} data-no-cell-edit>
            <Checkbox
              size="small"
              checked={selectedIds.has(rowId)}
              onChange={(event) =>
                toggleRowSelected(rowId, event.target.checked)
              }
            />
          </Box>
        )
      },
      [selectedIds, toggleRowSelected],
    )

    // --- Columns -------------------------------------------------------------

    // The photo / geo-tag / bulk-action cells are controls rather than values.
    // They are handed to the builder by field and draw only while their column
    // is part of COLUMN_CONFIG.
    const controlRenderers = useMemo(
      () => ({
        stationPhoto: createUploadPhotoCellRenderer({
          bulkEditMode,
          onStaged: bumpEditVersion,
        }),
        gpsCoordinates: createGPSCoordinatesCellRenderer({
          bulkEditMode,
          onOpenGPSPicker: handleOpenGPSPicker,
        }),
        delete: createBulkActionCellRenderer({
          bulkEditMode,
          isDark,
          onEditItem,
          onDeleteItem,
        }),
      }),
      [bulkEditMode, isDark, handleOpenGPSPicker, onEditItem, onDeleteItem, bumpEditVersion],
    )

    const columns = useMemo<GridColumn<StationRow>[]>(() => {
      const built = buildColumns({
        bulkEditMode,
        interfaceId,
        isDark,
        t,
        units,
        showController: Boolean(isControlHub),
        areaOptionsData,
        subAreaOptionsData,
        plantOptionsData,
        soilOptionsData,
        emitterOptionsData,
        pipeOptionsData,
        etZoneOptionsData,
        submitValidatedRef,
        getMergedRows,
        cellHighlights,
        onViewItem,
        onDeleteItem,
        onSaveRow: handleSaveRow,
        onDiscardRow: handleDiscardRow,
        isNonIrrigation,
        controlRenderers,
        guidedSetup: draftFromDecoders,
        conflictingStationIds,
        savedAddresses,
        issueReason,
      })

      // SCRUM-1032: none of these earn their width in the wizard. Bulk select
      // drives a bulk delete the step does not offer; the detail drawer opens a
      // station that has not been created yet; and micro climate is an ET zone
      // refinement, not something a station needs before it exists. All three
      // stay on /config.
      const HIDDEN_IN_GUIDED = ['viewDetail', 'etZone']
      // The action column holds only the conflict DELETE now that per-row Save
      // and discard are gone, so it earns its width only when a row has that
      // conflict. Otherwise it is an empty strip against the right edge.
      if (conflictingStationIds.size === 0) HIDDEN_IN_GUIDED.push('viewAction')
      const visible = draftFromDecoders
        ? built.filter((column) => !HIDDEN_IN_GUIDED.includes(column.field))
        : built

      if (!bulkEditMode || draftFromDecoders) return visible

      const selectColumn: GridColumn<StationRow> = {
        field: '__select',
        headerName: '',
        pinned: 'left',
        lockPosition: true,
        width: 50,
        minWidth: 50,
        maxWidth: 50,
        flex: 0,
        sortable: false,
        resizable: false,
        editable: false,
        headerRenderer: SelectHeaderCheckbox,
        cellRenderer: SelectCellCheckbox,
        cellStyle: { padding: 0 },
      }

      return [selectColumn, ...visible]
    }, [
      bulkEditMode,
      draftFromDecoders,
      isDark,
      t,
      units,
      isControlHub,
      areaOptionsData,
      subAreaOptionsData,
      plantOptionsData,
      soilOptionsData,
      emitterOptionsData,
      pipeOptionsData,
      etZoneOptionsData,
      getMergedRows,
      cellHighlights,
      onViewItem,
      onDeleteItem,
      handleSaveRow,
      handleDiscardRow,
      controlRenderers,
      conflictingStationIds,
      savedAddresses,
      issueReason,
      interfaceId,
      SelectHeaderCheckbox,
      SelectCellCheckbox,
    ])

    const columnGroups = useMemo<GridColumnGroup[]>(
      () =>
        COLUMN_GROUPS.map((group) => ({
          id: group.groupId,
          headerName: t(group.headerName),
          headerClass: classes.colGroup,
        })),
      [t],
    )

    const defaultSort = useMemo(
      () => ({ field: DEFAULT_SORT_FIELD, sort: 'asc' as const }),
      [],
    )

    // --- Column visibility ---------------------------------------------------

    // The saved map is applied once the grid exists, then re-applied whenever
    // bulk edit changes which of the two mode-driven columns belong on screen.
    useEffect(() => {
      const api = gridApiRef.current
      if (!api) return
      COLUMN_CONFIG.forEach((column) => {
        api.setColumnsVisible(
          [column.field],
          visibleColumns[column.field] !== false,
        )
      })
      // Status frees up room in bulk-edit mode; the DELETE column only exists
      // there. Outside bulk edit the column setting decides, so the loop above
      // keeps the last word.
      if (bulkEditMode) api.setColumnsVisible(['state'], false)
      api.setColumnsVisible(['viewAction'], bulkEditMode)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bulkEditMode, visibleColumns])

    // --- Infinite scroll -----------------------------------------------------

    const handleReachEnd = useCallback(() => {
      if (hasNextPage && !isFetchingNextPage) fetchNextPage()
    }, [hasNextPage, isFetchingNextPage, fetchNextPage])

    // For double-click fill: every remaining page, one after another, so the
    // fill reaches the last station rather than the last one scrolled in.
    const loadAllRows = useCallback(async () => {
      // A failed page resolves rather than throwing, and leaves `hasNextPage`
      // true — looping on that alone retries a broken page for ever, and the
      // promise never settles, so the fill never runs and the grid sits behind
      // its overlay. Stop on the first failure; the fill then covers what did
      // load. The page cap is a guard against a datasource that always says
      // there is more, not a real limit.
      const maxPages = 500
      for (let page = 0; page < maxPages; page++) {
        const result = await fetchNextPage()
        if (result.isError || !result.hasNextPage) return
      }
    }, [fetchNextPage])

    // A first page shorter than the viewport would never scroll, so it would
    // never ask for the next one.
    useEffect(() => {
      if (!hasNextPage || isFetchingNextPage || rows.length === 0) return
      const timeoutId = setTimeout(() => {
        const viewport = gridApiRef.current?.getViewportEl()
        if (!viewport) return
        if (viewport.scrollHeight <= viewport.clientHeight) fetchNextPage()
      }, 300)
      return () => clearTimeout(timeoutId)
    }, [rows.length, hasNextPage, isFetchingNextPage, fetchNextPage])

    // --- Status bar ----------------------------------------------------------

    // The grid sizes itself against the VIEWPORT, not this flex column, so it
    // has no idea the status bar sits below it and would happily take the last
    // pixel — pushing the counts off the bottom of the page. Measuring the bar
    // and handing its height over as the gap is what keeps everything on one
    // screen with only the list scrolling.
    const statusBarRef = useRef<HTMLDivElement>(null)
    const [statusBarHeight, setStatusBarHeight] = useState(0)

    useLayoutEffect(() => {
      const el = statusBarRef.current
      if (!el) return
      const measure = () => setStatusBarHeight(el.offsetHeight)
      measure()
      const observer = new ResizeObserver(measure)
      observer.observe(el)
      return () => observer.disconnect()
    }, [])

    const totalStations =
      listStationData?.pages?.[0]?.totalCount || rows.length || 0

    // Same four groups the status filter offers.
    //
    // Counted by the endpoint rather than from `rows`, because /config pages:
    // counting the loaded rows reported "Running 0" for a controller with
    // twenty running, since none of them were in the first twenty by name. The
    // wizard holds the whole list already, so it counts what it has and asks
    // for nothing.
    const serverStatusCounts = useStationStatusCounts(
      interfaceId,
      serverFilters,
      keyword,
      !externalMode && !draftFromDecoders,
    )

    const localStatusCounts = useMemo(() => {
      const counts = { running: 0, waitStart: 0, error: 0, idling: 0 }
      displayedRows.forEach((row) => {
        counts[stationStateBucket(row?.state)] += 1
      })
      return counts
    }, [displayedRows])

    const statusCounts =
      externalMode || draftFromDecoders ? localStatusCounts : serverStatusCounts

    // --- Imperative handle ---------------------------------------------------

    useImperativeHandle(
      ref,
      () => ({
        handleSave: async () => {
          // Same gate as the autosave: on Next this waits for a write already
          // in flight rather than starting a second one over the same rows.
          await runSerialised(handleSave)
        },
        // Through the same gate, so a flush started while the debounced
        // autosave is mid-write queues behind it and then picks up whatever
        // that run left staged — rather than returning at once and letting the
        // wizard change step out from under the write (Wei Woon, 21 Sep).
        flushReadyRows: async () => {
          // A cell editor left open holds a value that is not staged yet, and
          // this is the one save path the autosave's `isEditing` guard does not
          // cover — it runs because the user clicked away, which is exactly
          // when an editor is still open. Closed first, as `handleSave` does,
          // so the flush writes what the user typed and not what was there
          // before they typed it.
          commitOpenEditor(gridApiRef.current)
          // Leaving right after entering: the option lists or the entry reads
          // may still be on their way. Waited for (the Station step opts out of
          // the wizard's flush timeout); if a list failed to load, nothing can
          // be written and the step is not reported as saved.
          const deadline = Date.now() + 60_000
          while (!isWriteReady() && writeReadyPendingRef.current) {
            if (Date.now() >= deadline) break
            await new Promise((resolve) => setTimeout(resolve, 200))
          }
          if (!isWriteReady()) return { failed: 1, total: 1 }
          return runSerialised(runAutosave)
        },
        handleOpenColumnVisibility,
        handleBasicListView,
        isBasicListActive,
        applyPreset: handleApplyPreset,
        applyColumns: applySavedView,
        removeRows: (ids: string[]) => {
          const idSet = new Set(ids)
          ids.forEach((id) => gridApiRef.current?.removePendingEdit(id))
          setRows((previous) =>
            previous.filter((row) => !idSet.has(String(row.id))),
          )
          setSelectedIds((previous) => {
            const next = new Set(previous)
            ids.forEach((id) => next.delete(id))
            return next
          })
        },
      }),
      [
        handleSave,
        runAutosave,
        runSerialised,
        isWriteReady,
        handleOpenColumnVisibility,
        handleBasicListView,
        isBasicListActive,
        handleApplyPreset,
        applySavedView,
      ],
    )

    // --- Render --------------------------------------------------------------

    return (
      <Box
        sx={{
          // Fill the flex parent down to the page bottom so the footer sits at
          // the bottom and the grid extends to meet it.
          height: '100%',
          minHeight: 0,
          width: '100%',
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <Stack
          sx={{
            height: '100%',
            width: '100%',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{ flex: 1, width: '100%', minWidth: 0, minHeight: 0 }}
            onKeyDownCapture={sayLockedWhileWriting}
            onClickCapture={sayLockedWhileWriting}
          >
            <DataGrid<StationRow>
              columns={columns}
              columnGroups={columnGroups}
              groupHeaderHeight={GROUP_HEADER_HEIGHT}
              rows={displayedRows}
              getRowId={getRowId}
              postSort={postSort}
              apiRef={gridApiRef}
              rowHeight={ROW_HEIGHT}
              headerHeight={HEADER_HEIGHT}
              wrapHeaderText
              centerContent
              defaultSort={defaultSort}
              // No editing while the rows are being written: an edit typed
              // during a save is collected by nobody and dropped when the save
              // clears the staged rows (SCRUM-1010 QC items 3 and 4).
              //
              // The autosave's writes too: a row typed into while its pass was
              // out lost the value (SCRUM-1032 round 7, item 2).
              editMode={
                bulkEditMode && !isSaving && !isAutosaveWriting
                  ? 'singleClick'
                  : 'none'
              }
              loadingBlocksInput={isSaving}
              batchEdit={bulkEditMode}
              cellFocus
              onCellValueChanged={onCellValueChanged}
              onFilterChanged={setFilterModel}
              onReachEnd={handleReachEnd}
              isFetchingMore={isFetchingNextPage}
              loadAllRows={hasNextPage ? loadAllRows : undefined}
              loading={isSaving || (isLoadingStations && !externalMode)}
              columnStateStorageKey={COLUMN_STATE_KEY}
              columnStateUnprefixed
              viewportBoundGap={statusBarHeight + VIEWPORT_BOUND_GAP}
              rowClass={rowClass}
              // Passed on /config too: the wizard's own toggles stay off there,
              // and the "still to fill in" filter works on both screens.
              rowFilter={rowFilter}
              onSortChanged={handleSortChanged}
              // Only when the endpoint did the sorting. Re-sorting the loaded
              // prefix by the grid's comparator would fight the order the rest
              // of the pages arrive in.
              sortOnServer={Boolean(serverSort) && !needsWholeList}
              className={bulkEditMode ? classes.editModeGrid : undefined}
            />
          </Box>

          {/* SCRUM-1032: the wizard's own toolbar already reports what this
              step is about — how many stations are saved and what still needs
              attention. The live status counts are an operations readout for a
              controller that is running, not one being set up. */}
          {!draftFromDecoders && (
            <Stack
              ref={statusBarRef}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                flexShrink: 0,
                px: 3,
                py: 1.75,
                backgroundColor: 'background.default',
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography
                  sx={{ fontSize: '13px', color: 'grey.600', fontWeight: 500 }}
                >
                  <Box
                    component="span"
                    sx={{ color: 'text.main', fontWeight: 700 }}
                  >
                    {totalStations}
                  </Box>{' '}
                  {t('stationsCountUnit')}
                </Typography>
                {isFetchingNextPage && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} />
                    <Typography className={classes.txtPlant}>
                      {t('commonLoadingMore')}
                    </Typography>
                  </Stack>
                )}
              </Stack>

              <Stack direction="row" spacing={1.25} alignItems="center">
                {/* Each pill tints its own background with the same colour it
                    writes on, so the 500 weights land at 2.9-4.1:1 either way.
                    Dark gets the lighter 400s (matching the grid cells in
                    column-defs-builder), light gets the darker 700s. */}
                {[
                  {
                    labelKey: 'stationsStateRunning',
                    count: statusCounts.running,
                    color: isDark ? '#4ADE80' : '#166534',
                  },
                  {
                    labelKey: 'stationsStateWaitStart',
                    count: statusCounts.waitStart,
                    // Amber, the colour the row rail already gives Wait Start.
                    // Light gets the 700 rather than the rail's 500: this one
                    // is text, and the 500 does not carry on white.
                    color: isDark ? '#FBBF24' : '#B45309',
                  },
                  {
                    labelKey: 'stationsStateError',
                    count: statusCounts.error,
                    color: isDark ? '#F87171' : '#B91C1C',
                  },
                  {
                    labelKey: 'stationsStateIdling',
                    count: statusCounts.idling,
                    color: isDark ? '#94A3B8' : '#475569',
                  },
                ].map(({ labelKey, count, color }) => (
                  <Stack
                    key={labelKey}
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    sx={{
                      px: '10px',
                      py: '3px',
                      borderRadius: '999px',
                      backgroundColor: `${color}1F`,
                      border: `1px solid ${color}3D`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        backgroundColor: color,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: 600,
                        lineHeight: 1.2,
                        color,
                      }}
                    >
                      {t(labelKey)}
                      <Box component="span" sx={{ ml: '4px', fontWeight: 700 }}>
                        {count}
                      </Box>
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>

        <PinStationOnMap
          open={openGPSModal}
          station={currentGPSStation}
          interfaceId={Number(interfaceId)}
          onClose={handleCloseGPSPicker}
          onChangePosition={handleChangePosition}
        />

        <ColumnSettingPopover
          columns={COLUMN_CONFIG as any}
          anchorEl={columnVisibilityAnchor}
          onClose={handleCloseColumnVisibility}
          visibleColumns={visibleColumns}
          onToggleColumn={handleToggleColumn}
          onShowAll={handleShowAllColumns}
          onHideAll={handleHideAllColumns}
          alwaysVisibleFields={['description']}
          excludeFields={['viewDetail', 'viewAction']}
          getColumnState={() => gridApiRef.current?.getColumnState() ?? []}
        />
      </Box>
    )
  },
)

StationTableView.displayName = 'StationTableView'

export default StationTableView
